import React, { useState, useEffect, useMemo } from "react";
import {
  FiActivity, FiZap, FiDollarSign, FiClock, FiCpu, FiLayers,
  FiRefreshCw, FiChevronDown, FiChevronUp, FiArrowUp, FiArrowDown,
  FiFilter, FiAlertTriangle, FiChevronLeft, FiChevronRight,
  FiTarget
} from "react-icons/fi";
import "../assets/css/query-analyzer.css";

const NEXT_API_URL = process.env.REACT_APP_NEXT_API_URL || "http://localhost:3000";
const PAGE_SIZE = 15;

const formatNumber = (num) => {
  if (num === null || num === undefined) return "-";
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

const formatCost = (cost) => {
  if (cost === null || cost === undefined || isNaN(cost)) return "-";
  if (cost === 0) return "$0.00";
  if (cost < 0.001) return `$${cost.toFixed(5)}`;
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(3)}`;
};

const formatTime = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

/** Normalize internal mode names to user-facing modes */
const normalizeMode = (mode) => {
  if (!mode) return "smart"; // pre-mode-system queries
  if (mode === "smart-multi-agent") return "smart";
  if (mode === "genius-multi-agent") return "genius";
  return mode;
};

const getModeConfig = (mode) => {
  const configs = {
    simple: { label: "Simple", color: "#3b82f6", bg: "rgba(59,130,246,0.1)", icon: FiZap },
    smart: { label: "Smart", color: "#8b5cf6", bg: "rgba(139,92,246,0.1)", icon: FiCpu },
    genius: { label: "Genius", color: "#d97706", bg: "rgba(217,119,6,0.1)", icon: FiLayers },
    unknown: { label: "Unknown", color: "#9ca3af", bg: "rgba(156,163,175,0.1)", icon: FiActivity },
  };
  return configs[normalizeMode(mode)] || configs.unknown;
};

function QueryAnalyzer({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [modeFilter, setModeFilter] = useState("all");
  const [days, setDays] = useState(7);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = async (isRefresh = false) => {
    if (!userId) return;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      // Always fetch all modes; filter client-side after normalizing MA variants
      const params = new URLSearchParams({ user_id: userId, limit: "500", days: days.toString() });

      const res = await fetch(`${NEXT_API_URL}/api/usage/query-analyzer?${params}`);
      if (!res.ok) throw new Error("Failed to fetch");
      const json = await res.json();
      if (json.success) {
        setData(json.data);
      } else {
        throw new Error(json.error || "Unknown error");
      }
    } catch (err) {
      console.error("QueryAnalyzer fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (userId) fetchData();
  }, [userId, days]);

  const queries = data?.queries || [];
  const summary = data?.summary || {};

  // Merge multi-agent variants into parent modes for user-friendly display
  const comparison = useMemo(() => {
    const raw = data?.comparison || [];
    const merged = {};
    for (const c of raw) {
      const key = normalizeMode(c.mode);
      if (!merged[key]) {
        merged[key] = { ...c, mode: key };
      } else {
        const m = merged[key];
        const totalCount = m.count + c.count;
        m.avgTokens = Math.round((m.avgTokens * m.count + c.avgTokens * c.count) / totalCount);
        m.avgPrompt = Math.round((m.avgPrompt * m.count + c.avgPrompt * c.count) / totalCount);
        m.avgCompletion = Math.round((m.avgCompletion * m.count + c.avgCompletion * c.count) / totalCount);
        m.avgCost = (m.avgCost * m.count + c.avgCost * c.count) / totalCount;
        m.avgLatency = Math.round((m.avgLatency * m.count + c.avgLatency * c.count) / totalCount);
        m.totalCost = m.totalCost + c.totalCost;
        m.totalTokens = m.totalTokens + c.totalTokens;
        m.count = totalCount;
      }
    }
    return Object.values(merged);
  }, [data?.comparison]);

  // Client-side mode filter (after normalizing MA variants)
  const filteredQueries = useMemo(() => {
    if (modeFilter === "all") return queries;
    return queries.filter(q => normalizeMode(q.mode) === modeFilter);
  }, [queries, modeFilter]);

  const totalPages = Math.ceil(filteredQueries.length / PAGE_SIZE);
  const paginatedQueries = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredQueries.slice(start, start + PAGE_SIZE);
  }, [filteredQueries, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [filteredQueries]);

  const toggleExpand = (id) => {
    const next = new Set(expandedRows);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpandedRows(next);
  };

  const comparisonSummary = useMemo(() => {
    const parts = [];
    for (const c of comparison) {
      if (c.count > 0) {
        const cfg = getModeConfig(c.mode);
        parts.push(`${cfg.label}: ${formatNumber(c.avgTokens)} avg`);
      }
    }
    if (parts.length < 2) return null;
    return { label: parts.join(" | ") };
  }, [comparison]);

  if (loading) {
    return (
      <div className="qa-container">
        <div className="qa-loading">
          <div className="qa-loading-spinner" />
          <p>Analyzing queries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="qa-container">
        <div className="qa-error">
          <FiAlertTriangle size={28} />
          <h4>Unable to load query data</h4>
          <p>{error}</p>
          <button className="qa-btn qa-btn-primary" onClick={() => fetchData()}>
            <FiRefreshCw size={14} /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="qa-container">
      {/* Header */}
      <div className="qa-header">
        <div>
          <h2 className="qa-title">Query Analyzer</h2>
          <p className="qa-subtitle">Deep analysis — Simple vs Smart vs Genius mode, per-agent tokens, costs</p>
        </div>
        <div className="qa-header-actions">
          <select className="qa-select-small" value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={1}>Last 24h</option>
            <option value={3}>Last 3 days</option>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <button className="qa-btn qa-btn-outline" onClick={() => fetchData(true)} disabled={refreshing}>
            <FiRefreshCw size={14} className={refreshing ? "qa-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Summary Strip */}
      <div className="qa-summary-strip">
        <div className="qa-summary-item">
          <FiActivity size={16} />
          <span>{formatNumber(summary.totalQueries)} queries</span>
        </div>
        <div className="qa-summary-item">
          <FiDollarSign size={16} />
          <span>{formatCost(summary.totalCost)} total cost</span>
        </div>
        <div className="qa-summary-item">
          <FiZap size={16} />
          <span>{formatNumber(summary.totalTokens)} tokens</span>
        </div>
        <div className="qa-summary-item">
          <FiClock size={16} />
          <span>{days} day{days !== 1 ? "s" : ""} period</span>
        </div>
      </div>

      {/* Mode Comparison Cards */}
      {comparison.length > 0 && (
        <div className="qa-section">
          <h3 className="qa-section-title">
            <FiTarget size={18} />
            Mode Comparison
            {comparisonSummary && (
              <span className="qa-savings-badge neutral">
                {comparisonSummary.label}
              </span>
            )}
          </h3>
          <div className="qa-comparison-grid">
            {comparison.map((m) => {
              const config = getModeConfig(m.mode);
              const ModeIcon = config.icon;
              return (
                <div key={m.mode} className="qa-mode-card" style={{ borderColor: config.color }}>
                  <div className="qa-mode-header" style={{ background: config.bg }}>
                    <ModeIcon size={18} style={{ color: config.color }} />
                    <span className="qa-mode-label" style={{ color: config.color }}>{config.label}</span>
                    <span className="qa-mode-count">{m.count} queries</span>
                  </div>
                  <div className="qa-mode-body">
                    <div className="qa-mode-stat-row">
                      <span className="qa-mode-stat-label">Avg Tokens</span>
                      <span className="qa-mode-stat-value">{formatNumber(m.avgTokens)}</span>
                    </div>
                    <div className="qa-mode-stat-row">
                      <span className="qa-mode-stat-label">Avg Input</span>
                      <span className="qa-mode-stat-value">{formatNumber(m.avgPrompt)}</span>
                    </div>
                    <div className="qa-mode-stat-row">
                      <span className="qa-mode-stat-label">Avg Output</span>
                      <span className="qa-mode-stat-value">{formatNumber(m.avgCompletion)}</span>
                    </div>
                    <div className="qa-mode-stat-row">
                      <span className="qa-mode-stat-label">Avg Cost</span>
                      <span className="qa-mode-stat-value qa-green">{formatCost(m.avgCost)}</span>
                    </div>
                    <div className="qa-mode-stat-row">
                      <span className="qa-mode-stat-label">Avg Latency</span>
                      <span className="qa-mode-stat-value">{m.avgLatency}ms</span>
                    </div>
                    <div className="qa-mode-stat-row qa-mode-stat-total">
                      <span className="qa-mode-stat-label">Total Cost</span>
                      <span className="qa-mode-stat-value qa-green-bold">{formatCost(m.totalCost)}</span>
                    </div>
                    {m.topModel && (
                      <div className="qa-mode-stat-row">
                        <span className="qa-mode-stat-label">Top Model</span>
                        <span className="qa-mode-stat-value qa-model-text">{m.topModel.split("/").pop()}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="qa-filter-bar">
        <FiFilter size={14} />
        <span>Filter by mode:</span>
        {["all", "simple", "smart", "genius"].map(m => (
          <button
            key={m}
            className={`qa-filter-btn ${modeFilter === m ? "active" : ""}`}
            onClick={() => setModeFilter(m)}
            style={modeFilter === m && m !== "all" ? { background: getModeConfig(m).bg, color: getModeConfig(m).color, borderColor: getModeConfig(m).color } : {}}
          >
            {m === "all" ? "All" : getModeConfig(m).label}
          </button>
        ))}
      </div>

      {/* Per-Query Table */}
      <div className="qa-table-card">
        <div className="qa-table-wrapper">
          <table className="qa-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>Time</th>
                <th>Query</th>
                <th>Mode</th>
                <th>Model</th>
                <th>Input</th>
                <th>Output</th>
                <th>Total</th>
                <th>Cost</th>
                <th>Latency</th>
              </tr>
            </thead>
            <tbody>
              {paginatedQueries.length === 0 ? (
                <tr>
                  <td colSpan="10" className="qa-empty">No query data found for this period</td>
                </tr>
              ) : (
                paginatedQueries.map((q) => {
                  const mc = getModeConfig(q.mode);
                  const isExpanded = expandedRows.has(q.id);
                  return (
                    <React.Fragment key={q.id}>
                      <tr className={`qa-row ${isExpanded ? "expanded" : ""}`} onClick={() => toggleExpand(q.id)}>
                        <td className="qa-expand-cell">
                          {isExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                        </td>
                        <td className="qa-td-time">{formatTime(q.time)}</td>
                        <td className="qa-td-query" title={q.queryPreview}>{q.queryPreview ? q.queryPreview.substring(0, 60) + (q.queryPreview.length > 60 ? "..." : "") : "-"}</td>
                        <td><span className="qa-mode-pill" style={{ background: mc.bg, color: mc.color }}>{mc.label}</span></td>
                        <td className="qa-td-model" title={q.model}>{q.model?.split("/").pop()?.split("-").slice(0, 3).join("-") || "-"}</td>
                        <td className="qa-td-num">{formatNumber(q.promptTokens)}</td>
                        <td className="qa-td-num">{formatNumber(q.completionTokens)}</td>
                        <td className="qa-td-num qa-td-total">{formatNumber(q.totalTokens)}</td>
                        <td className="qa-td-cost">{formatCost(q.costUsd)}</td>
                        <td className="qa-td-latency">{q.latencyMs ? `${q.latencyMs}ms` : "-"}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="qa-expanded-row">
                          <td colSpan="10">
                            <div className="qa-expanded-content">
                              <div className="qa-detail-grid">
                                <div className="qa-detail-item">
                                  <span className="qa-detail-label">Full Query</span>
                                  <pre className="qa-detail-pre">{q.queryPreview || "N/A"}</pre>
                                </div>
                                <div className="qa-detail-row">
                                  <div className="qa-detail-item">
                                    <span className="qa-detail-label">Provider</span>
                                    <span className="qa-detail-value">{q.provider}</span>
                                  </div>
                                  <div className="qa-detail-item">
                                    <span className="qa-detail-label">Model</span>
                                    <span className="qa-detail-value">{q.model}</span>
                                  </div>
                                </div>
                                <div className="qa-detail-row">
                                  <div className="qa-detail-item">
                                    <span className="qa-detail-label">TTFT</span>
                                    <span className="qa-detail-value">{q.ttftMs ? `${q.ttftMs}ms` : "N/A"}</span>
                                  </div>
                                  <div className="qa-detail-item">
                                    <span className="qa-detail-label">Finish Reason</span>
                                    <span className="qa-detail-value">{q.finishReason || "N/A"}</span>
                                  </div>
                                  <div className="qa-detail-item">
                                    <span className="qa-detail-label">Cache Read</span>
                                    <span className="qa-detail-value">{q.cacheReadTokens > 0 ? formatNumber(q.cacheReadTokens) : "0"}</span>
                                  </div>
                                  <div className="qa-detail-item">
                                    <span className="qa-detail-label">Cache Write</span>
                                    <span className="qa-detail-value">{q.cacheWriteTokens > 0 ? formatNumber(q.cacheWriteTokens) : "0"}</span>
                                  </div>
                                </div>
                                {/* Token cost breakdown */}
                                <div className="qa-detail-item">
                                  <span className="qa-detail-label">Token & Cost Breakdown</span>
                                  <div className="qa-breakdown-bar">
                                    <div className="qa-breakdown-segment qa-seg-input" style={{ flex: q.promptTokens || 1 }} title={`Input: ${formatNumber(q.promptTokens)}`}>
                                      <FiArrowUp size={10} /> {formatNumber(q.promptTokens)}
                                    </div>
                                    <div className="qa-breakdown-segment qa-seg-output" style={{ flex: q.completionTokens || 1 }} title={`Output: ${formatNumber(q.completionTokens)}`}>
                                      <FiArrowDown size={10} /> {formatNumber(q.completionTokens)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="qa-pagination">
            <span className="qa-pagination-info">
              {((currentPage - 1) * PAGE_SIZE) + 1}-{Math.min(currentPage * PAGE_SIZE, filteredQueries.length)} of {filteredQueries.length}
            </span>
            <div className="qa-pagination-btns">
              <button className="qa-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                <FiChevronLeft size={14} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push(<span key={`e-${p}`} className="qa-page-dots">...</span>);
                  acc.push(<button key={p} className={`qa-page-btn ${currentPage === p ? "active" : ""}`} onClick={() => setCurrentPage(p)}>{p}</button>);
                  return acc;
                }, [])}
              <button className="qa-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                <FiChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default QueryAnalyzer;
